import { ITheme } from '@/helpers/theme';
import { Component } from 'vue-property-decorator';
import EditorBase from '../EditorBase';
import ComponentHelper, { EComponentType, IComponent, IImageComponent, ISLWidgetComponent } from '@/helpers/editor';
import { cloneDeep } from 'lodash';
import NotificationHelper from '@/helpers/notification';
import { EAnalyticsEvent } from '@/helpers/analytic';

const BASE_HORIZONTAL_MARGIN = 32;
const BASE_PROJECT_HEIGHT = 720;

@Component({})
export default class ThemeLibraryMixins extends EditorBase {
  previewTheme: ITheme = null;
  isConfirmationView: boolean = false;

  applyToAlertBox: boolean = false;
  applyToChatBox: boolean = false;
  applyToEventList: boolean = false;

  get scaleFactor(): number {
    return this.projectHeight / BASE_PROJECT_HEIGHT;
  }

  get applyToAll(): boolean {
    return this.applyToAlertBox && this.applyToChatBox && this.applyToEventList;
  }

  resetThemeModal() {
    this.previewTheme = null;
    this.isConfirmationView = false;
    this.applyToAlertBox = false;
    this.applyToChatBox = false;
    this.applyToEventList = false;
  }

  setPreviewTheme(theme: ITheme) {
    this.previewTheme = theme;
  }

  setThemeToInstall(theme: ITheme) {
    this.themeToInstall = theme;
  }

  createOrUpdateChatComponent(useSLSettings = false) {
    const existingComponent = this.components.find((component: IComponent) => component.type === EComponentType.chat);

    // return if widget doesn't exist already and toggle is true
    if (!existingComponent && useSLSettings) return;

    const component: ISLWidgetComponent = existingComponent ? cloneDeep(existingComponent) : ComponentHelper.defaultChatSettings;

    if (!useSLSettings) {
      component.w = Math.round(360 * this.scaleFactor);
      component.h = Math.round(128 * this.scaleFactor);
      // left side
      component.x = Math.round(25 * this.scaleFactor);
      component.y = Math.round(560 * this.scaleFactor);
    }

    component.useSLSettings = useSLSettings;

    if (existingComponent) {
      this.onComponentUpdate(component);
    } else {
      const components = cloneDeep(this.components);

      components.unshift(component);
      this.components = components;
    }
  }

  createOrUpdateEventListComponent(useSLSettings = false) {
    const existingComponent = this.components.find((component: IComponent) => component.type === EComponentType.eventList);

    // return if widget doesn't exist already and toggle is true
    if (!existingComponent && useSLSettings) return;

    const component: ISLWidgetComponent = existingComponent ? cloneDeep(existingComponent) : ComponentHelper.defaultEventListSettings;

    if (!useSLSettings) {
      component.w = Math.round(400 * this.scaleFactor);
      component.h = Math.round(139 * this.scaleFactor);
      // right side
      component.x = Math.round(this.projectWidth - component.w - (BASE_HORIZONTAL_MARGIN * this.scaleFactor));
      component.y = Math.round(this.projectHeight - component.h - (32 * this.scaleFactor));
    }

    component.useSLSettings = useSLSettings;

    if (existingComponent) {
      this.onComponentUpdate(component);
    } else {
      const components = cloneDeep(this.components);

      components.unshift(component);
      this.components = components;
    }
  }

  createOrUpdateAlertBoxComponent(useSLSettings = false) {
    const existingComponent = this.components.find((component: IComponent) => component.type === EComponentType.alertBox);

    // return if widget doesn't exist already and toggle is true
    if (!existingComponent && useSLSettings) return;

    const component: ISLWidgetComponent = existingComponent ? cloneDeep(existingComponent) : ComponentHelper.defaultAlertBoxSettings;

    if (!useSLSettings) {
      component.w = Math.round(600 * this.scaleFactor);
      component.h = Math.round(200 * this.scaleFactor);
      // right side
      component.x = Math.round(this.projectWidth - component.w - (BASE_HORIZONTAL_MARGIN * this.scaleFactor));
      component.y = 32;
      component.lockAspectRatio = false;
    }

    component.useSLSettings = useSLSettings;

    if (existingComponent) {
      this.onComponentUpdate(component);
    } else {
      const components = cloneDeep(this.components);

      components.unshift(component);
      this.components = components;
    }
  }

  createOrUpdateBorderComponent() {
    const existingComponent = this.components.find((component: IImageComponent) => component.type === EComponentType.image && component.isBorder);
    const component: IImageComponent = existingComponent ? cloneDeep(existingComponent) : ComponentHelper.defaultImageSettings;

    component.w = this.projectWidth;
    component.h = this.projectHeight;
    component.x = 0;
    component.y = 0;
    component.locked = true;
    component.isBorder = true;
    component.value = this.themeToInstall.images.background;
    component.lockAspectRatio = false;
    component.verticalSettings = {
      x: 0,
      y: 0,
      w: this.projectHeight,
      h: this.projectWidth,
    };

    if (existingComponent) {
      this.onComponentUpdate(component);
    } else {
      const components = cloneDeep(this.components);

      components.push(component);
      this.components = components;
    }

    this.hasUnsavedChange = true;
  }

  getThumbnail(link) {
    return link.replace('overlays-cdn.streamlabs.com', 'cdn.streamlabsstudio.com');
  }

  installTheme() {
    this.createOrUpdateAlertBoxComponent(this.applyToAlertBox);
    this.createOrUpdateEventListComponent(this.applyToEventList);
    this.createOrUpdateChatComponent(this.applyToChatBox);
    this.createOrUpdateBorderComponent();

    const scene = cloneDeep(this.selectedScene);
    scene.theme_id = this.themeToInstall.id;

    const components = cloneDeep(this.components);

    components.sort((componentA, componentB) => {
      const sortIndexA = componentA.sortIndex ?? 999;
      const sortIndexB = componentB.sortIndex ?? 999;

      return sortIndexB - sortIndexA;
    });

    scene.components = components;
    this.selectedScene = scene;
    this.hasUnsavedChange = true;

    NotificationHelper.success('Theme applied!  Save the scene to update the overlay.');
    this.$sla.sendEvent(EAnalyticsEvent.themeAdded, { id: this.themeToInstall.id });
  }

  toggleAll(value) {
    this.applyToAlertBox = value;
    this.applyToChatBox = value;
    this.applyToEventList = value;
  }
}
